import React, { useState } from 'react';
import { doc, setDoc } from 'firebase/firestore';
import { firestore } from '../../backend/firebaseConfig';
import { createAdmin } from '../../Controllers/adminController';
import { motion } from 'framer-motion';
import { XIcon, UserPlusIcon } from 'lucide-react';
import SuccessDialog from '../DialogHandling/SuccessDialog';
import ErrorDialog from '../DialogHandling/ErrorDialog';

const CreateAdminForm = ({ setShowCreateForm }) => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [showSuccessDialog, setShowSuccessDialog] = useState(false);
  const [showErrorDialog, setShowErrorDialog] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Check if the password is at least 6 characters long
    if (password.length < 6) {
      setErrorMessage('Password must be at least 6 characters long.');
      setShowErrorDialog(true);
      return;
    }

    // Check if the phone number is exactly 11 characters long
    if (phone.length !== 11) {
      setErrorMessage('Phone number must be exactly 11 characters long.');
      setShowErrorDialog(true);
      return;
    }

    try {
      const AdminCredential = await createAdmin({ email, password });
      console.log("Admin created successfully:", AdminCredential.data.user);
      const uid = AdminCredential.data.user;
      await setDoc(doc(firestore, 'admins', uid), {
        uid,
        name,
        email,
        phone,
      });
      setSuccessMessage('Admin created successfully!');
      setShowSuccessDialog(true);
      setTimeout(() => setShowCreateForm(false), 2000);  // Close the form automatically after 2 seconds
    } catch (error) {
      console.error("Error creating Admin:", error.message);
      setErrorMessage(`Error creating Admin: ${error.message}`);
      setShowErrorDialog(true);
    }
  };

  return (
    <div className="fixed inset-0 bg-gradient-to-br from-indigo-500/50 to-purple-500/50 backdrop-blur-sm flex justify-center items-center overflow-y-auto">
      <motion.div
        initial={{ opacity: 0, y: -50 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
        className="relative bg-white dark:bg-gray-800 p-8 rounded-2xl shadow-2xl max-w-md w-full m-4"
      >
        <h3 className="text-2xl font-bold mb-6 text-gray-800 dark:text-white text-center">
          Create New Admin
        </h3>
        <form className="space-y-4" onSubmit={handleSubmit}>
          <div>
            <label htmlFor="name" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Name
            </label>
            <input
              type="text"
              id="name"
              name="name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 focus:ring-2 focus:ring-indigo-500 dark:focus:ring-purple-500 focus:border-transparent dark:bg-gray-700 dark:text-white transition duration-200"
              required
            />
          </div>
          <div>
            <label htmlFor="email" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Email
            </label>
            <input
              type="email"
              id="email"
              name="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 focus:ring-2 focus:ring-indigo-500 dark:focus:ring-purple-500 focus:border-transparent dark:bg-gray-700 dark:text-white transition duration-200"
              required
            />
          </div>
          <div>
            <label htmlFor="phone" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Phone
            </label>
            <input
              type="text"
              id="phone"
              name="phone"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              className="w-full px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 focus:ring-2 focus:ring-indigo-500 dark:focus:ring-purple-500 focus:border-transparent dark:bg-gray-700 dark:text-white transition duration-200"
              required
            />
          </div>
          <div>
            <label htmlFor="password" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Password
            </label>
            <input
              type="password"
              id="password"
              name="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 focus:ring-2 focus:ring-indigo-500 dark:focus:ring-purple-500 focus:border-transparent dark:bg-gray-700 dark:text-white transition duration-200"
              required
            />
          </div>
          <div className="flex items-center justify-between pt-4">
            <button
              type="submit"
              className="flex items-center px-6 py-3 bg-indigo-600 hover:bg-indigo-700 text-white font-semibold rounded-lg transition duration-200 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 dark:focus:ring-offset-gray-800"
            >
              <UserPlusIcon className="w-5 h-5 mr-2" />
              Create Admin
            </button>
            <button
              type="button"
              onClick={() => setShowCreateForm(false)}
              className="flex items-center px-6 py-3 bg-gray-200 hover:bg-gray-300 text-gray-800 font-semibold rounded-lg transition duration-200 focus:outline-none focus:ring-2 focus:ring-gray-400 focus:ring-offset-2 dark:bg-gray-700 dark:text-gray-200 dark:hover:bg-gray-600 dark:focus:ring-gray-500 dark:focus:ring-offset-gray-800"
            >
              <XIcon className="w-5 h-5 mr-2" />
              Cancel
            </button>
          </div>
        </form>

        {showSuccessDialog && (
          <SuccessDialog
            message={successMessage}
            onClose={() => setShowSuccessDialog(false)}
            isOpen={showSuccessDialog}
          />
        )}
        {showErrorDialog && (
          <ErrorDialog
            message={errorMessage}
            onClose={() => setShowErrorDialog(false)}
            isOpen={showErrorDialog}
          />
        )}
      </motion.div>
    </div>
  );
};

export default CreateAdminForm;

